import { useState, useRef, useCallback, useEffect, memo } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';

interface MeetingAudioPlayerProps {
  src: string;
  onSeek?: (time: number) => void;
}

const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

const VIDEO_EXTENSIONS = /\.(mp4|webm|mkv|mov|avi)(\?|$)/i;

function detectVideo(url: string): boolean {
  if (VIDEO_EXTENSIONS.test(url)) return true;
  try { return new URL(url).searchParams.get('type') === 'video'; } catch { return false; }
}

export const MeetingAudioPlayer = memo(function MeetingAudioPlayer({
  src,
  onSeek,
}: MeetingAudioPlayerProps) {
  const isVideo = detectVideo(src);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animationRef = useRef<number | null>(null);
  const mediaRef = isVideo ? videoRef : audioRef;

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateProgress = useCallback(() => {
    const audio = mediaRef.current;
    if (!audio || !duration) return;
    setProgress((audio.currentTime / duration) * 100);
    if (!audio.paused && !audio.ended) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, [duration]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, updateProgress]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setProgress(0);
    setDuration(0);
    setIsPlaying(false);
    setPlaybackSpeed(1);
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = mediaRef.current;
    if (!audio || error || loading) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => setError(true));
    }
  }, [isPlaying, error, loading]);

  const cycleSpeed = useCallback(() => {
    const idx = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const newSpeed = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
    setPlaybackSpeed(newSpeed);
    if (mediaRef.current) mediaRef.current.playbackRate = newSpeed;
  }, [playbackSpeed]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = mediaRef.current;
      if (!audio || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      audio.currentTime = pos * duration;
      setProgress(pos * 100);
      onSeek?.(pos * duration);
    },
    [duration, onSeek]
  );

  /** Seek to a specific time in seconds (used by transcript click) */
  const seekTo = useCallback((timeSeconds: number) => {
    const audio = mediaRef.current;
    if (!audio) return;
    audio.currentTime = timeSeconds;
    setProgress((timeSeconds / duration) * 100);
    if (!isPlaying) {
      audio.play().catch(() => setError(true));
    }
  }, [duration, isPlaying]);

  // Expose seekTo via ref-like pattern
  useEffect(() => {
    (window as any).__meetingAudioSeekTo = seekTo;
    return () => { delete (window as any).__meetingAudioSeekTo; };
  }, [seekTo]);

  const currentTime = (progress / 100) * duration;

  const mediaEventHandlers = {
    onPlay: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onLoadedMetadata: () => {
      if (mediaRef.current) { setDuration(mediaRef.current.duration); setLoading(false); }
    },
    onCanPlayThrough: () => setLoading(false),
    onEnded: () => { setIsPlaying(false); setProgress(0); setPlaybackSpeed(1); if (mediaRef.current) mediaRef.current.playbackRate = 1; },
    onError: () => { setError(true); setLoading(false); },
  };

  // Se l'URL non e' un media riproducibile (es. share link Grain pubblico) il
  // browser fallisce sul caricamento — nascondiamo silenziosamente il player
  // invece di mostrare un banner di errore inutile (l'utente clicca il link al
  // recording originale dal "Guarda su Grain" o equivalente).
  if (error) return null;

  // Video player: native controls + custom speed button
  if (isVideo) {
    return (
      <div className="space-y-2">
        <video
          ref={videoRef}
          src={src}
          controls
          preload="metadata"
          className="w-full rounded-lg bg-black max-h-[400px]"
          {...mediaEventHandlers}
        />
        {!loading && (
          <div className="flex items-center justify-between px-1">
            <span className="text-[10px] text-[#787774] dark:text-[#999999]">
              {formatTime(duration)}
            </span>
            <button
              onClick={cycleSpeed}
              className="h-6 px-2 rounded-md bg-[rgba(55,53,47,0.08)] dark:bg-white/10 hover:bg-[rgba(55,53,47,0.16)] dark:hover:bg-white/20 transition-colors"
            >
              <span className="font-bold text-[10px] text-[#37352F] dark:text-white">
                {playbackSpeed}x
              </span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // Audio player: custom controls
  return (
    <div className="flex items-center gap-2 bg-[rgba(55,53,47,0.04)] dark:bg-[#2F2F2F] rounded-lg px-3 py-2">
      {/* Play/Pause */}
      <button
        onClick={togglePlay}
        disabled={loading}
        className={`flex-shrink-0 flex items-center justify-center rounded-full transition-colors w-8 h-8 ${
          loading
            ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10'
            : 'bg-red-600 hover:bg-red-700'
        }`}
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 text-[#787774] animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-3.5 h-3.5 text-white" />
        ) : (
          <Play className="w-3.5 h-3.5 text-white ml-0.5" />
        )}
      </button>

      {/* Progress + Time */}
      <div className="flex-1 flex flex-col gap-0.5 min-w-0">
        <div
          className="h-1.5 bg-[rgba(55,53,47,0.12)] dark:bg-white/15 rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-red-600 rounded-full transition-[width] duration-75"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between">
          <span className="text-[10px] text-[#787774] dark:text-[#999999]">
            {formatTime(currentTime)}
          </span>
          <span className="text-[10px] text-[#787774] dark:text-[#999999]">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* Speed */}
      <button
        onClick={cycleSpeed}
        className="flex-shrink-0 h-6 px-2 rounded-md bg-[rgba(55,53,47,0.08)] dark:bg-white/10 hover:bg-[rgba(55,53,47,0.16)] dark:hover:bg-white/20 transition-colors"
      >
        <span className="font-bold text-[10px] text-[#37352F] dark:text-white">
          {playbackSpeed}x
        </span>
      </button>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        {...mediaEventHandlers}
      />
    </div>
  );
});
